import { useEffect, useMemo, useState } from "react";
import Button from "../../components/button/Button";
import Table, { Column, Pagination } from "../../components/table/Table";
import { WaitlistRequest, RequestStatus } from "../../types";
import styles from "./WaitlistRequests.module.scss";
import RequestViewModal from "./RequestViewModal";
import { savePaginationSize } from "../../utils/pagination-helpers";
import { useSelector } from "react-redux";
import {
  fetchWaitlistRequestsForOrganization,
  setPagination,
  setRequestOnView,
  setSelectedStatus,
  deleteWaitlistRequest,
} from "../../slices/WaitlistRequestsSlice";
import { RootState, useAppDispatch } from "../../store";
import Select from "../../components/select/Select";
import { pageDomain, renderMap } from "./waitlist-requests.constants";
import { statusOptions } from "./waitlist-requests.constants";
import Toaster from "./Toaster";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";

const WaitlistRequests = () => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const { pagination, filteredWaitlistRequests, requestOnView, isLoading, selectedStatus } =
    useSelector((state: RootState) => state.waitlistRequests);

  const columns: Column<WaitlistRequest>[] = [
    {
      title: "Name",
      key: "firstName",
      dataIndex: "firstName",
      render: (_, record: WaitlistRequest) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Date of Birth",
      key: "dateOfBirth",
      dataIndex: "dateOfBirth",
      render: (dateOfBirth: string) => {
        // Parse the date without timezone conversion
        const date = DateTime.fromFormat(dateOfBirth, "yyyy-MM-dd");
        return date.toFormat("MM/dd/yyyy");
      },
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: renderMap.status,
    },
    {
      title: "Request Date",
      key: "createdAt",
      dataIndex: "createdAt",
      render: renderMap.createdAt,
    },
    {
      title: "",
      key: "requestId",
      dataIndex: "requestId",
      width: 300,
      absorbEvents: true,
      render: (_, record: WaitlistRequest) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" type="bordered" onClick={() => dispatch(setRequestOnView(record))}>
            View Details
          </Button>
          <Button 
            size="small" 
            type="primary" 
            onClick={() => dispatch(deleteWaitlistRequest({ requestId: record.requestId }))}
          >
            Mark as Resolved
          </Button>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (user?.token) {
      dispatch(fetchWaitlistRequestsForOrganization({ token: user.token }));
    }
  }, [dispatch, user]);

  // Sort requests by createdAt date, most recent first
  const sortedRequests = useMemo(
    () => [...filteredWaitlistRequests].sort((a, b) => {
      return DateTime.fromISO(b.createdAt).toMillis() - DateTime.fromISO(a.createdAt).toMillis();
    }),
    [filteredWaitlistRequests]
  );

  return (
    <main className={styles.container}>
      <h1 className={styles.title}>Waitlist Requests</h1>
      <div className="flex column gam-sm">
        <span>Request Status</span>
        <Select
          options={statusOptions}
          value={selectedStatus || []}
          onChange={(value) => dispatch(setSelectedStatus(value as RequestStatus[]))}
          placeholder="All Requests"
          className={styles.select}
          multiple
        />
      </div>
      <Table
        columns={columns}
        dataSource={sortedRequests}
        onPageChange={(pageNumber) => dispatch(setPagination({ ...pagination, pageNumber }))}
        onPageSizeChange={(pageSize) => {
          dispatch(setPagination({ ...pagination, pageSize, pageNumber: 1 }));
          savePaginationSize(pageDomain, pageSize);
        }}
        pagination={pagination}
        paginateOnBrowser
        loading={isLoading}
      />
      <RequestViewModal
        visible={Boolean(requestOnView)}
        onClose={() => dispatch(setRequestOnView(null))}
        request={requestOnView}
      />
      <Toaster />
    </main>
  );
};

export default WaitlistRequests;
